import _, {chain, orderBy, uniqBy} from 'lodash';

import {defineMessage} from '../../../libs/shared/helpers/src/internationalization/message';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {LocalizeRenderer} from '../../../srcCommon/internationalization/internationalizationContext';
import {ChannelReplyModesEnum} from '../../models/api/channelBehaviorsModel';
import {ChannelModel} from '../../models/api/channelModel';
import {buildContactUid, ContactSourcesEnum} from '../../models/api/contactModel';
import {MessageTypesEnum} from '../../models/api/messageModel';
import {RecipientContactMatchModel} from '../../models/api/recipientMatchModel';
import {RecipientModel, RecipientRolesEnum} from '../../models/api/recipientModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {emailRecipientParser, RecipientParserOptions, recipientParsers} from './recipientParsers';

export type RecipientHandle = {uid: string; role: RecipientRolesEnum};
export type AddRecipients = HandlerOf<ReadonlyArray<VisualRecipientModel>>;
export type RemoveRecipients = HandlerOf<ReadonlyArray<RecipientHandle>>;

/*
 * Visual recipients.
 */

/** Visual recipients grouped by role. */
export type VisualRecipientMap = {[key in RecipientRolesEnum]?: ReadonlyArray<VisualRecipientModel>};

export function buildRecipientsFromVisual(
  recipients: ReadonlyArray<VisualRecipientModel>,
): ReadonlyArray<RecipientModel> {
  return recipients.map((r) => ({
    contactUid: r.contact.uid,
    role: r.role,
    name: r.name,
    isSpammer: r.isSpammer,
  }));
}

export function buildVisualFromChannel(channel: ChannelModel): VisualRecipientModel {
  const contact = {
    uid: buildContactUid(channel.contactSource, channel.sendAddress),
    name: channel.displayName,
    source: channel.contactSource,
    handle: channel.sendAddress,
    channelId: channel.id,
    cardIds: {},
  };

  return {
    role: RecipientRolesEnum.FROM,
    contact,
    name: channel.displayName,
    namespace: channel.namespace,
  };
}

export function flattenRecipientMap(recipientMap: VisualRecipientMap) {
  return _(recipientMap).values().compact().flatten().value();
}

export function findDomainNames(recipients: VisualRecipientMap) {
  return _(flattenRecipientMap(recipients))
    .filter((r) => r.contact.source === ContactSourcesEnum.EMAIL)
    .filter((r) => r.role !== RecipientRolesEnum.FROM && r.role !== RecipientRolesEnum.REPLY_TO)
    .map((r) => r.contact.handle.replace(/.*@/, ''))
    .uniq()
    .sort()
    .value();
}

// TODO: Decide on whether we should prefer card or recipient name. Right now it's inconsistent. (PB-10603)
export function getRecipientName(
  recipient: VisualRecipientModel,
  shouldPreferCard = false,
): string | undefined {
  if (shouldPreferCard) {
    return (recipient.card && recipient.card.name) || recipient.name;
  }

  return recipient.name || (recipient.card && recipient.card.name);
}

export function isRecipientEqualToHandle(recipient: RecipientModel, handle: RecipientHandle) {
  return recipient.contactUid === handle.uid && recipient.role === handle.role;
}

export function isVisualRecipientEqualToHandle(
  visualRecipient: VisualRecipientModel,
  handle: RecipientHandle,
) {
  return visualRecipient.contact.uid === handle.uid && visualRecipient.role === handle.role;
}

/*
 * Compose.
 */

export function buildComposeRecipients(
  channel: ChannelModel,
  recipients: ReadonlyArray<VisualRecipientModel>,
): ReadonlyArray<VisualRecipientModel> {
  const fromRecipient = buildVisualFromChannel(channel);
  return [fromRecipient, ...recipients, ...findAutoBccRecipients(channel)];
}

/*
 * Reply.
 */

export enum ReplyTypesEnum {
  SINGLE = 'SINGLE',
  ALL = 'ALL',
  INTRO = 'INTRO',
  FORWARD = 'FORWARD',
}

export function buildReplyRecipients(
  participatingChannels: ReadonlyArray<ChannelModel>,
  autoBccHandlesFromRecipients: ReadonlyArray<string>,
  channel: ChannelModel,
  replyType: ReplyTypesEnum,
  sourceRecipients: ReadonlyArray<VisualRecipientModel>,
  isInbound: boolean,
) {
  // Filter and sort the recipients.
  const filteredRecipients = filterReplyRecipients(autoBccHandlesFromRecipients, channel, sourceRecipients);
  const sortedRecipients = sortReplyRecipients(participatingChannels, isInbound, filteredRecipients);

  // Update their role and return.
  return uniqBy(updateReplyRecipients(channel, replyType, sortedRecipients), (r) => r.contact.uid);
}

function filterReplyRecipients(
  autoBccHandlesFromRecipients: ReadonlyArray<string>,
  replyChannel: ChannelModel,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  // Check if we have a non-channel recipient, and if we have a "reply-to" recipient.
  const hasNonChannelRecipient = recipients.some((r) => r.channel !== replyChannel);
  const hasReplyToRecipient = recipients.some((r) => r.role === RecipientRolesEnum.REPLY_TO);

  // Filter the recipients according to various criteria.
  return recipients.filter((r) => {
    if (autoBccHandlesFromRecipients.includes(r.contact.handle) && r.role === RecipientRolesEnum.BCC) {
      return false;
    }

    // If we have non-channel recipients, remove the channel recipients.
    if (hasNonChannelRecipient && r.channel === replyChannel) {
      return false;
    }

    // Receive-only channels cannot be replied to.
    if (r.channel?.behaviors.replyMode === ChannelReplyModesEnum.UNSUPPORTED) {
      return false;
    }

    // If we have a "reply-to" recipient, remove any "from" recipient.
    if (hasReplyToRecipient && r.role === RecipientRolesEnum.FROM) {
      return false;
    }

    return true;
  });
}

/** Find the type priority of a given recipient. 1 > 4 */
function findRecipientTypePriority(
  participatingChannels: ReadonlyArray<ChannelModel>,
  recipient: VisualRecipientModel,
) {
  // Channel we have access to.
  const {channel} = recipient;
  if (channel && participatingChannels.includes(channel)) {
    return 4;
  }

  // Channel we don't have access to.
  if (channel) {
    return 3;
  }

  // Contact without card.
  if (!recipient.card) {
    return 2;
  }

  // Contact with card.
  return 1;
}

const inboundRolePriority = [
  RecipientRolesEnum.REPLY_TO,
  RecipientRolesEnum.FROM,
  RecipientRolesEnum.CC,
  RecipientRolesEnum.BCC,
  RecipientRolesEnum.TO,
];

const outboundRolePriority = [
  RecipientRolesEnum.REPLY_TO,
  RecipientRolesEnum.TO,
  RecipientRolesEnum.CC,
  RecipientRolesEnum.BCC,
  RecipientRolesEnum.FROM,
];

/** Sort the recipients by priority. */
function sortReplyRecipients(
  participatingChannels: ReadonlyArray<ChannelModel>,
  isMessageInbound: boolean,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  // Find the priority map to use depending on the direction of the message.
  const rolePriorities = isMessageInbound ? inboundRolePriority : outboundRolePriority;

  return orderBy(recipients, [
    (r) => rolePriorities.indexOf(r.role),
    (r) => findRecipientTypePriority(participatingChannels, r),
  ]);
}

/** Rewrite to role of the recipients to be used in a reply. */
function updateReplyRecipients(
  channel: ChannelModel,
  replyType: ReplyTypesEnum,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  switch (channel.messageType) {
    case MessageTypesEnum.EMAIL: {
      const replyRecipients = updateEmailReplyRecipients(replyType, recipients);
      return addAutoBccRecipients(channel, replyRecipients);
    }

    case MessageTypesEnum.SMS:
    case MessageTypesEnum.TWEET:
      return overwriteRecipientRole(recipients);

    // Otherwise, just keep the highest priority recipient.
    default: {
      const toRecipient = recipients[0] && {...recipients[0], role: RecipientRolesEnum.TO};
      return (toRecipient && [toRecipient]) || [];
    }
  }
}

const preserveRoles = [RecipientRolesEnum.CC, RecipientRolesEnum.BCC];
function updateEmailReplyRecipients(
  replyType: ReplyTypesEnum,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  switch (replyType) {
    // Forward an email.
    case ReplyTypesEnum.FORWARD: // TODO: Add auto-bcc recipients.
      return [];

    // Reply-all to an email.
    case ReplyTypesEnum.ALL:
      return recipients.map((r) => {
        // If it's one of the roles we should preserve, return as-is.
        if (preserveRoles.includes(r.role)) {
          return r;
        }

        // Otherwise, move to 'To'.
        return {...r, role: RecipientRolesEnum.TO};
      });

    // Reply-all to an introduction email.
    case ReplyTypesEnum.INTRO: {
      // Did the email have a 'To' recipient other than ourselves?
      const hasToRecipients = recipients.some((r) => r.role === RecipientRolesEnum.TO);

      return recipients.map((r) => {
        switch (r.role) {
          // Move sender to 'Bcc'.
          case RecipientRolesEnum.FROM:
            return {...r, role: RecipientRolesEnum.BCC};

          // If there were no other 'To' recipients, move 'Cc' to 'To'.
          case RecipientRolesEnum.CC:
            return !hasToRecipients ? {...r, role: RecipientRolesEnum.TO} : r;

          // Keep the rest intact.
          default:
            return r;
        }
      });
    }
    // Reply to an email.
    default: {
      const toRecipient = recipients[0] && {...recipients[0], role: RecipientRolesEnum.TO};
      return (toRecipient && [toRecipient]) || [];
    }
  }
}

function overwriteRecipientRole(recipients: ReadonlyArray<VisualRecipientModel>) {
  // Always "reply-all".
  return recipients.map((r) => ({...r, role: RecipientRolesEnum.TO}));
}

export function findIntroducerRecipient(
  replyToRecipients: ReadonlyArray<VisualRecipientModel>,
  recipients: ReadonlyArray<RecipientModel>,
): VisualRecipientModel | undefined {
  const sender = replyToRecipients.find((r) => r.role === RecipientRolesEnum.FROM);
  if (!sender) {
    return undefined;
  }

  const wasSenderMovedToBcc = recipients.some((r) => r.contactUid === sender.contact.uid);
  if (!wasSenderMovedToBcc) {
    return undefined;
  }

  return sender;
}

/*
 * Parsing.
 */

export function parseRecipient(
  source: ContactSourcesEnum,
  role: RecipientRolesEnum,
  text: string,
  options?: RecipientParserOptions,
) {
  // Find the parser for the specified source.
  const parser = recipientParsers[source];
  if (!parser) {
    throw new Error('A parser for the provided source could not be found.');
  }

  // Normalize the text.
  const recipientText = text.trim().toLowerCase();

  // Parse the text as a recipient.
  return parser(role, recipientText, options);
}

/*
 * Auto-completion.
 */

export function buildVisualRecipientFromMatch(
  namespace: string,
  role: RecipientRolesEnum,
  recipientMatch: RecipientContactMatchModel,
): VisualRecipientModel {
  return {
    contact: {
      uid: recipientMatch.uid,
      source: recipientMatch.source,
      handle: recipientMatch.handle,
      cardIds: {},
    },
    role,
    name: recipientMatch.name,
    namespace,
  };
}

export function findRecipientForVariableInterpolation(
  recipients: VisualRecipientModel[],
): VisualRecipientModel | undefined {
  const recipientRolePriority = [RecipientRolesEnum.TO, RecipientRolesEnum.CC, RecipientRolesEnum.BCC];
  return chain(recipients)
    .filter((r) => recipientRolePriority.includes(r.role))
    .sortBy((r) => recipientRolePriority.indexOf(r.role))
    .first()
    .value();
}

/*
 * Auto-Bcc.
 */

function findAutoBccRecipients(channel: ChannelModel) {
  const {autoBcc} = channel.preferences;
  if (channel.messageType !== MessageTypesEnum.EMAIL || !autoBcc.isEnabled) {
    return [];
  }

  return _(autoBcc.handles)
    .map((r) => emailRecipientParser(RecipientRolesEnum.BCC, r))
    .compact()
    .value();
}

function addAutoBccRecipients(channel: ChannelModel, recipients: ReadonlyArray<VisualRecipientModel>) {
  const autoBccRecipients = findAutoBccRecipients(channel);
  if (autoBccRecipients.length === 0) {
    return recipients;
  }

  const recipientsToAdd = autoBccRecipients.filter(
    (bcc) => !recipients.some((r) => r.contact.handle === bcc.contact.handle),
  );

  return [...recipients, ...recipientsToAdd];
}

export function computeNewChannelRecipients(
  newChannel: ChannelModel,
  previousChannel: ChannelModel,
  recipients: VisualRecipientMap,
) {
  const allRecipients = flattenRecipientMap(recipients);
  const fromLessRecipients = allRecipients.filter((recipient) => recipient.role !== RecipientRolesEnum.FROM); // Removing previous 'from' recipients, the new one will be added on the backend side.
  return maybeReplaceAutoBcc(newChannel, previousChannel, fromLessRecipients);
}

export function maybeReplaceAutoBcc(
  newChannel: ChannelModel,
  previousChannel: ChannelModel,
  recipients: ReadonlyArray<VisualRecipientModel>,
) {
  const newAutoBcc = findAutoBccRecipients(newChannel);
  const previousAutoBcc = findAutoBccRecipients(previousChannel);

  // No changes.
  if (newAutoBcc.length === 0 && previousAutoBcc.length === 0) {
    return undefined;
  }

  const previousBccUids = previousAutoBcc.map((r) => r.contact.uid);

  return (
    _(recipients)
      // Remove BCC recipients from the previous channel.
      .filter((r) => r.role !== RecipientRolesEnum.BCC || !previousBccUids.includes(r.contact.uid))
      // Add the ones from the new channel.
      .concat(newAutoBcc)
      // Dedupe.
      .uniqBy((r) => r.contact.uid)
      .value()
  );
}

/*
 * Localization.
 */

export function formatRecipientRole(localize: Localizer, role: RecipientRolesEnum) {
  const roleResolver = recipientRoleNames[role];
  return (roleResolver && roleResolver(localize)) || undefined;
}

type RecipientRoleNames = {[T in RecipientRolesEnum]?: LocalizeRenderer};
const recipientRoleNames: RecipientRoleNames = {
  [RecipientRolesEnum.FROM]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-from',
        message: 'From',
      }),
    ),
  [RecipientRolesEnum.TO]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-to',
        message: 'To',
      }),
    ),
  [RecipientRolesEnum.CC]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-cc',
        message: 'Cc',
      }),
    ),
  [RecipientRolesEnum.BCC]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-bcc',
        message: 'Bcc',
      }),
    ),
  [RecipientRolesEnum.REPLY_TO]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-replyto',
        message: 'Reply-To',
      }),
    ),
  [RecipientRolesEnum.MENTION]: (localize) =>
    localize(
      defineMessage({
        id: 'recipient-role-to',
        message: 'To',
      }),
    ),
};
